import { ExternalLink, LogOut, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { filterNav, NAV_ITEMS, NAV_SECTIONS, ROLE_LABELS } from '@/lib/navigation'
import { useAuth } from '@/contexts/AuthContext'
import { MAIN_SITE_URL } from '@/lib/config'

export function Sidebar({ open, onClose }: { open?: boolean; onClose?: () => void }) {
  const { permissions, staff, logout } = useAuth()
  const items = filterNav(NAV_ITEMS, permissions)
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 900px)').matches)
  useEffect(() => {
    const media = window.matchMedia('(max-width: 900px)')
    const update = () => setMobile(media.matches)
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  return (
    <aside className={cn('admin-sidebar', open && 'is-open')} aria-label="Admin navigation" inert={mobile && !open}>
      <div className="admin-sidebar-head">
        <NavLink to="/" className="admin-brand" onClick={onClose}>
          <span className="admin-brand-mark">c</span>
          <span><strong>clyr</strong><small>control room</small></span>
        </NavLink>
        <button type="button" className="admin-sidebar-close" onClick={onClose} aria-label="Close navigation">
          <X aria-hidden="true" />
        </button>
      </div>

      <nav className="admin-nav">
        {NAV_SECTIONS.map((section) => {
          const sectionItems = items.filter((item) => item.section === section)
          if (!sectionItems.length) return null
          return (
            <section className="admin-nav-section" key={section} aria-labelledby={`nav-${section}`}>
              <h2 id={`nav-${section}`}>{section}</h2>
              <div>
                {sectionItems.map((item) => (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    end={item.path === '/'}
                    onClick={onClose}
                    title={item.description}
                    className={({ isActive }) => cn('admin-nav-link', isActive && 'is-active')}
                  >
                    <item.icon aria-hidden="true" />
                    <span>{item.shortLabel || item.label}</span>
                  </NavLink>
                ))}
              </div>
            </section>
          )
        })}
      </nav>

      <div className="admin-sidebar-foot">
        <div className="admin-staff-summary">
          <span className="admin-staff-avatar">{(staff?.role || 'S').charAt(0).toUpperCase()}</span>
          <span>
            <strong>{staff ? ROLE_LABELS[staff.role] || staff.role : 'Staff'}</strong>
            <small>{staff?.title || 'Internal access'}</small>
          </span>
        </div>
        <div className="admin-sidebar-actions">
          <a href={MAIN_SITE_URL} title="Open clyr" aria-label="Open clyr"><ExternalLink aria-hidden="true" /></a>
          <button type="button" onClick={logout} title="Log out" aria-label="Log out"><LogOut aria-hidden="true" /></button>
        </div>
      </div>
    </aside>
  )
}
